import {
    ChangeDetectorRef,
    Component,
    Injector,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { NgbActiveModal, NgbModal, NgbModalRef } from '@cmps/ng-bootstrap';
import { CalendarView } from 'angular-calendar';
import { from, of, Subject } from 'rxjs';
import { catchError, takeUntil, tap } from 'rxjs/operators';

import { AuthService } from '../../../auth/auth.service';
import {
    PopUpComponent,
    PopUpService,
} from '../../../coaching-observation/experimental.pop-up';
import { StateToProps } from '../../../store';
import { useDispatcher, UseStore } from '../../../store.decorator';
import { TranslationKeyMap } from '../../coaching-insight.i18n';
import {
    EventFormAction,
    EventParticipant,
} from '../models/schedule-event.model';
import { ScheduleEventContainerComponent } from '../schedule-event/schedule-event.container.component';
import { SelectOptionCalendarComponent } from '../select-option-calendar/select-option-calendar.component';

import {
    initEvent,
    rootChangeView,
    rootInit,
    rootReload,
} from './calendar.action';
import coachingCalendarReducer from './calendar.reducer';
import { RootState } from './calendar.state';

@Component({
    selector: '[cmpsCalendar]',
    templateUrl: './calendar.component.html',
    // changeDetection: ChangeDetectionStrategy.OnPush, // will try without this for now
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'cmps-calendar-component',
        'data-e2e': 'component-calendar',
    },
    styles: [
        `
            .cmps-event-style span {
                color: #ffffff !important;
            }

            .cmps-calendar-update-modal .modal-dialog .modal-content {
                border: rgba(0, 0, 0, 0);
                background: rgba(0, 0, 0, 0);
            }
            .cmps-calendar-update-modal [cmpsscheduleeventcontainer] {
                margin: 0 auto;
            }
        `,
    ],
})
@UseStore<RootState>(
    'coachingInsight.coachingCalendar',
    coachingCalendarReducer.coachingCalendar,
)
export class CalendarComponent implements OnInit, StateToProps<RootState> {
    state: RootState;

    /** @private */ _i18n = TranslationKeyMap;
    /** input */ managerId: number;
    /** input */ performerId: number;
    /** input */ performerUser: EventParticipant;
    /** @private */ _refresh: Subject<void>;

    // select option
    private _ref: PopUpComponent<SelectOptionCalendarComponent>;
    private _modalUpdateEventRef: NgbModalRef;

    constructor(
        private readonly cdr: ChangeDetectorRef,
        public activeModal: NgbActiveModal,
        private readonly _popUpService: PopUpService,
        private readonly _authService: AuthService,
        private readonly _injector: Injector,
        private readonly _modalService: NgbModal,
    ) {
        this._refresh = new Subject();
    }

    ngOnInit(): void {
        useDispatcher(this, rootInit({ managerId: this.managerId }));
        useDispatcher(
            this,
            rootReload({ performerId: this.performerUser.userId }),
        );
    }

    ngOnDestroy(): void {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._ref && this._ref.dismiss();
    }

    stateToProps(prevState?: RootState): void {
        if (prevState.calendarEvents !== this.state.calendarEvents) {
            // this._refresh.next();
        }
    }

    /** @private */ _onViewChange($event: CalendarView): void {
        const ranks = {
            [CalendarView.Month]: 3,
            [CalendarView.Week]: 2,
            [CalendarView.Day]: 1,
        };
        const cr = ranks[this.state.view];
        const nr = ranks[$event];

        // if zooming in and assuming viewDate isn't about to change too
        // TODO revise reload anyway
        if (cr >= nr) {
            useDispatcher(this, rootChangeView({ view: $event }));
        } else {
            useDispatcher(
                this,
                rootReload({
                    performerId: this.performerUser.userId,
                    view: $event,
                }),
            );
        }
    }

    /** Catch output of calendar-navigation and dispatch action to load new events using the new period */
    /** @private */ _onChangeNavigation($event: Date): void {
        useDispatcher(
            this,
            rootReload({
                performerId: this.performerUser.userId,
                view: null,
                viewDate: $event,
            }),
        );
    }

    eventClicked(event: any): void {
        const target = event.sourceEvent.target as HTMLElement;
        useDispatcher(
            this,
            initEvent({ eventId: event.event.meta.scheduledEventId }),
        );
        // const eventId = event
        const ref = (this._ref = this._popUpService.show(
            SelectOptionCalendarComponent,
            {
                target,
                placement: 'bottom-left',
                backdrop: false,
                containerClass: 'soc-pu-container',
                autoClose: true,
            },
        ));

        ref.componentRef.instance.updateEventChange
            .pipe(
                tap((next) => {
                    this._updateEvent();
                    ref.close();
                }),
                catchError(() => of(null)),
                takeUntil(from(ref.result)),
            )
            .subscribe({
                complete: () => {
                    this._ref = null;
                },
            });

        ref.componentRef.instance.cancelEventChange
            .pipe(
                tap((next) => {
                    this._cancelEvent();
                    ref.close();
                }),
                catchError(() => of(null)),
                takeUntil(from(ref.result)),
            )
            .subscribe({
                complete: () => {
                    this._ref = null;
                },
            });
    }

    hourEventClicked(event: any): void {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._modalUpdateEventRef && this._modalUpdateEventRef.dismiss();
        this._modalUpdateEventRef = this._modalService.open(
            ScheduleEventContainerComponent,
            {
                injector: this._injector,
                windowClass: 'cmps-calendar-update-modal',
                backdrop: false,
            },
        );

        const component = this._modalUpdateEventRef
            .componentInstance as ScheduleEventContainerComponent;
        component.actionOnForm = EventFormAction.NEWWITHSLOT;
        component.startDate = event.date;
        component.requiredPerformer = this.performerUser;
        component.calendarEvents = this.state.calendarEvents;
        // component.eventId = this.state.eventId;
        component.closed
            .pipe(
                catchError(() => of(null)),
                takeUntil(from(this._modalUpdateEventRef.result)),
            )
            .subscribe(() => {
                this._modalUpdateEventRef.close();
                // reload events on calendar
                useDispatcher(
                    this,
                    rootReload({ performerId: this.performerUser.userId }),
                );
            });
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    _updateEvent() {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._modalUpdateEventRef && this._modalUpdateEventRef.dismiss();
        this._modalUpdateEventRef = this._modalService.open(
            ScheduleEventContainerComponent,
            {
                injector: this._injector,
                windowClass: 'cmps-calendar-update-modal',
                backdrop: false,
            },
        );

        const component = this._modalUpdateEventRef
            .componentInstance as ScheduleEventContainerComponent;
        component.actionOnForm = EventFormAction.UPDATE;
        component.eventId = this.state.eventId;
        component.calendarEvents = this.state.calendarEvents;

        component.closed
            .pipe(
                catchError(() => of(null)),
                takeUntil(from(this._modalUpdateEventRef.result)),
            )
            .subscribe(() => {
                this._modalUpdateEventRef.close();
                // reload events on calendar
                useDispatcher(
                    this,
                    rootReload({ performerId: this.performerUser.userId }),
                );
            });
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    _cancelEvent() {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._modalUpdateEventRef && this._modalUpdateEventRef.dismiss();
        this._modalUpdateEventRef = this._modalService.open(
            ScheduleEventContainerComponent,
            {
                injector: this._injector,
                windowClass: 'cmps-calendar-update-modal',
                backdrop: false,
            },
        );

        const component = this._modalUpdateEventRef
            .componentInstance as ScheduleEventContainerComponent;
        component.actionOnForm = EventFormAction.CANCEL;
        component.eventId = this.state.eventId;
        component.closed
            .pipe(
                catchError(() => of(null)),
                takeUntil(from(this._modalUpdateEventRef.result)),
            )
            .subscribe(() => {
                this._modalUpdateEventRef.close();
                // reload events on calendar
                useDispatcher(
                    this,
                    rootReload({ performerId: this.performerUser.userId }),
                );
            });
    }
}
